// General entity resolver — turns an opaque id into a human name, a context
// word, and a deep-link that opens the entity's DRAWER (not just the section).
// One home for the kind → route/label mapping, shared by AlertCard and any other
// surface that renders an entity ref. The API builds the same `?focus=`
// links server-side for System Health; this mirrors that convention on the client
// where only the raw alert fields are available.
//
// "show" is a deliberate alias of "production" in this domain — there is no
// separate show entity — so it collapses onto `production`.

import type {
  Alert,
} from '@/api/types';
import type {
  AlertCategory,
} from '@/api/types.gen';

export type EntityKind = 'production' | 'pixel' | 'venue' | 'event' | 'campaign';

/** A resolved entity reference: what kind of thing, and its id. */
export interface EntityRef {
  kind: EntityKind;
  id: string;
}

/** The route (section) an entity kind lives on. */
const KIND_ROUTE: Record<EntityKind, string> = {
  production: '/backend/pipeline',
  pixel: '/backend/pixels',
  venue: '/backend/venues',
  event: '/backend/events',
  campaign: '/backend/data-pipeline', // no dedicated screen yet — land on the closest section
};

/** Kinds whose section has a per-object detail drawer (so `?focus` opens it). */
const KIND_HAS_DRAWER: Record<EntityKind, boolean> = {
  production: true,
  pixel: true,
  venue: true,
  event: true,
  campaign: false, // screen parked — no drawer to open
};

/** Human word for the kind (the "what is this" context). */
export function entityKindLabel(kind: EntityKind): string {
  switch (kind) {
    case 'production':
      return 'Production';
    case 'pixel':
      return 'Pixel';
    case 'venue':
      return 'Venue';
    case 'event':
      return 'Event';
    case 'campaign':
      return 'Campaign';
  }
}

/**
 * Deep-link to an entity: `/backend/<section>?focus=<id>` when the kind has a
 * drawer, else the bare section route. `null` when there's no id.
 */
export function entityDrawerLink(kind: EntityKind, id: string | null | undefined): string | null {
  if (!id) return null;
  const route = KIND_ROUTE[kind];
  return KIND_HAS_DRAWER[kind] ? `${route}?focus=${encodeURIComponent(id)}` : route;
}

/**
 * Derive the entity a fired alert is ABOUT from its category + id fields. Returns
 * null for global/system incidents (no entity). `production_id` is preferred when
 * present; otherwise `scope_ref` carries the id for the category's kind.
 */
export function entityRefFromAlert(a: Pick<Alert, 'category' | 'production_id' | 'scope_ref'>): EntityRef | null {
  const kind = kindForCategory(a.category ?? null);
  if (!kind) return null;
  const id = (kind === 'production' ? a.production_id : null) ?? a.scope_ref ?? null;
  if (!id) return null;
  return { kind, id };
}

/** Which entity kind a rule/incident category scopes to (null = global). */
export function kindForCategory(category: AlertCategory | null): EntityKind | null {
  switch (category) {
    case 'backfill_quality':
      return 'production';
    case 'pixel':
      return 'pixel';
    case 'campaign_pacing':
    case 'roas':
      return 'campaign';
    default:
      return null; // system / null → no entity
  }
}
